import { c as _c } from "react/compiler-runtime";
import { type CopilotErrorEvent } from '@copilotkit/shared';
import { useCallback, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { get as getFromLocalStore, LOCAL_STORAGE_KEYS, remove as removeFromLocalStore, store as saveToLocalStore } from 'shared/lstore';
import logger from 'shared/logger';
import { v4 as uuidv4 } from 'uuid';
import { CHAT_HISTORY_QUERY_KEY } from 'shared/copilot/api';
import { type CopilotSurface } from 'shared/copilot/copilotSurfaces';
const log = logger().child({
  namespace: 'copilot/session'
});
export const getCopilotChatIdStorageKey = (userId: string, clientId: number, surface: CopilotSurface = 'drawer'): string => LOCAL_STORAGE_KEYS.COPILOT_CHAT_ID(userId, clientId, surface);

/** Pre-surface-split key used only by the drawer before `_drawer` suffix existed. */
export const getLegacyDrawerCopilotChatIdStorageKey = (userId: string, clientId: number): string => LOCAL_STORAGE_KEYS.COPILOT_CHAT_ID(userId, clientId, 'drawer').replace(/_drawer$/, '');

/**
 * Resolves the initial chat id for a surface. Drawer migrates the unsuffixed
 * legacy key onto `_drawer` once so later clears/new-chat cannot resurrect it.
 * Fullscreen identity comes from the `/ai/chat` URL path (via route sync), not localStorage.
 */
export const resolveInitialCopilotChatId = (userId: string, clientId: number, surface: CopilotSurface): string | null => {
  if (surface === 'fullscreen') {
    return null;
  }
  const storageKey = getCopilotChatIdStorageKey(userId, clientId, surface);
  const fromSurface = getFromLocalStore(storageKey);
  if (fromSurface != null) return fromSurface;
  const legacyStorageKey = getLegacyDrawerCopilotChatIdStorageKey(userId, clientId);
  const fromLegacy = getFromLocalStore(legacyStorageKey);
  if (fromLegacy == null) return null;
  saveToLocalStore(storageKey, fromLegacy);
  removeFromLocalStore(legacyStorageKey);
  return fromLegacy;
};

/** Reads the stored drawer chat id without migrating the legacy key. */
const readPersistedChatId = (userId: string, clientId: number): string | null => getFromLocalStore(getCopilotChatIdStorageKey(userId, clientId, 'drawer')) ?? getFromLocalStore(getLegacyDrawerCopilotChatIdStorageKey(userId, clientId));
const clearPersistedChatId = (userId: string, clientId: number, surface: CopilotSurface): void => {
  if (surface === 'fullscreen') return;
  removeFromLocalStore(getCopilotChatIdStorageKey(userId, clientId, surface));
  removeFromLocalStore(getLegacyDrawerCopilotChatIdStorageKey(userId, clientId));
};
interface UseCopilotSessionArgs {
  userId: number;
  clientId: number;
  surface: CopilotSurface;
  onError: (error: CopilotErrorEvent) => void;
  /** Chat id from `?copilotChatId=`; wins over localStorage so a link/refresh opens that exact chat. */
  urlChatId?: string | null;
  /** `?copilotChatId=` was present but not a chat id: start a new chat instead of the stored one. */
  urlChatIdInvalid?: boolean;
}
export interface CopilotSession {
  threadId: string;
  chatId: string | null;
  onError: (error: CopilotErrorEvent) => void;
  onChatIdChange: (chatId: string) => void;
  onNewChat: () => string;
  onLoadChat: (chatId: string) => void;
  onChatHydrated: (chatId: string) => void;
  onTitleGenerated: (chatId: string) => void;
  /**
   * Chat to fall back to when `failedChatId` is the still-unconfirmed `?copilotChatId=` chat:
   * the user's stored drawer chat, read at call time. `null` means start a new chat instead.
   */
  resolveFallbackChatId: (failedChatId: string) => string | null;
}

/**
 * Owns the long-lived state for a single copilot surface session (drawer or fullscreen).
 *
 * Drawer chat identity is persisted in localStorage. Fullscreen identity is owned by the
 * `/ai/chat` URL path (synced outside this hook). Agent threadId carries identity to Flow;
 * CopilotKit provider headers stay limited to user/client so concurrent surfaces do not
 * overwrite each other's X-Alvie-Chat-Id.
 */
export const useCopilotSession = t0 => {
  const $ = _c(58);
  const {
    userId,
    clientId,
    surface,
    onError,
    urlChatId: t1,
    urlChatIdInvalid: t2
  } = t0;
  const urlChatId = t1 === undefined ? null : t1;
  const urlChatIdInvalid = t2 === undefined ? false : t2;
  let storageKey;
  let t3;
  let userIdKey;
  if ($[0] !== clientId || $[1] !== surface || $[2] !== urlChatId || $[3] !== urlChatIdInvalid || $[4] !== userId) {
    userIdKey = userId.toString();
    storageKey = getCopilotChatIdStorageKey(userIdKey, clientId, surface);
    t3 = surface === "drawer" && urlChatIdInvalid ? null : (surface === "drawer" ? urlChatId : null) ?? resolveInitialCopilotChatId(userIdKey, clientId, surface);
    $[0] = clientId;
    $[1] = surface;
    $[2] = urlChatId;
    $[3] = urlChatIdInvalid;
    $[4] = userId;
    $[5] = storageKey;
    $[6] = t3;
    $[7] = userIdKey;
  } else {
    storageKey = $[5];
    t3 = $[6];
    userIdKey = $[7];
  }
  const initialChatId = t3;
  let t4;
  if ($[8] !== initialChatId) {
    t4 = () => initialChatId ?? uuidv4();
    $[8] = initialChatId;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  const [threadId, setThreadId] = useState(t4);
  let t5;
  if ($[10] !== initialChatId) {
    t5 = () => initialChatId;
    $[10] = initialChatId;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const [chatId, setChatId] = useState(t5);
  const queryClient = useQueryClient();
  const urlSeedUnconfirmedRef = useRef(surface === "drawer" && urlChatId != null && !urlChatIdInvalid);
  let t6;
  if ($[12] !== clientId || $[13] !== surface || $[14] !== urlChatId || $[15] !== userIdKey) {
    t6 = () => {
      if (urlSeedUnconfirmedRef.current) {
        const storedChatId = readPersistedChatId(userIdKey, clientId);
        if (storedChatId != null && storedChatId !== urlChatId) {
          return;
        }
      }
      clearPersistedChatId(userIdKey, clientId, surface);
    };
    $[12] = clientId;
    $[13] = surface;
    $[14] = urlChatId;
    $[15] = userIdKey;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  const clearPersistedUnlessProtected = t6;
  let t7;
  if ($[17] !== clientId || $[18] !== queryClient || $[19] !== storageKey || $[20] !== surface || $[21] !== urlChatId || $[22] !== userId) {
    t7 = newChatId => {
      log.info({
        chatId: newChatId,
        surface
      }, "copilot chat id changed");
      const isUnconfirmedUrlChat = urlSeedUnconfirmedRef.current && newChatId === urlChatId;
      if (!isUnconfirmedUrlChat) {
        urlSeedUnconfirmedRef.current = false;
        if (surface !== "fullscreen") {
          saveToLocalStore(storageKey, newChatId);
        }
      }
      setChatId(newChatId);
      queryClient.invalidateQueries({
        queryKey: [CHAT_HISTORY_QUERY_KEY, clientId, userId]
      });
    };
    $[17] = clientId;
    $[18] = queryClient;
    $[19] = storageKey;
    $[20] = surface;
    $[21] = urlChatId;
    $[22] = userId;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const onChatIdChange = t7;
  let t8;
  if ($[24] !== chatId || $[25] !== clearPersistedUnlessProtected || $[26] !== surface) {
    t8 = () => {
      const newThreadId = uuidv4();
      log.info({
        previousChatId: chatId,
        threadId: newThreadId,
        surface
      }, "copilot new chat");
      setThreadId(newThreadId);
      setChatId(null);
      clearPersistedUnlessProtected();
      return newThreadId;
    };
    $[24] = chatId;
    $[25] = clearPersistedUnlessProtected;
    $[26] = surface;
    $[27] = t8;
  } else {
    t8 = $[27];
  }
  const onNewChat = t8;
  let t9;
  if ($[28] !== storageKey || $[29] !== surface) {
    t9 = newChatId_0 => {
      log.info({
        chatId: newChatId_0,
        surface
      }, "copilot chat loaded");
      urlSeedUnconfirmedRef.current = false;
      setThreadId(newChatId_0);
      setChatId(newChatId_0);
      if (surface !== "fullscreen") {
        saveToLocalStore(storageKey, newChatId_0);
      }
    };
    $[28] = storageKey;
    $[29] = surface;
    $[30] = t9;
  } else {
    t9 = $[30];
  }
  const onLoadChat = t9;
  let t10;
  if ($[31] !== storageKey || $[32] !== surface) {
    t10 = hydratedChatId => {
      urlSeedUnconfirmedRef.current = false;
      if (surface !== "fullscreen") {
        saveToLocalStore(storageKey, hydratedChatId);
      }
    };
    $[31] = storageKey;
    $[32] = surface;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  const onChatHydrated = t10;
  let t11;
  if ($[34] !== clientId || $[35] !== queryClient || $[36] !== surface || $[37] !== userId) {
    t11 = titleChatId => {
      log.info({
        chatId: titleChatId,
        surface
      }, "copilot chat title generated");
      queryClient.invalidateQueries({
        queryKey: [CHAT_HISTORY_QUERY_KEY, clientId, userId]
      });
    };
    $[34] = clientId;
    $[35] = queryClient;
    $[36] = surface;
    $[37] = userId;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  const onTitleGenerated = t11;
  let t12;
  if ($[39] !== chatId || $[40] !== clearPersistedUnlessProtected || $[41] !== onError || $[42] !== surface) {
    t12 = error => {
      if (error.error.code !== "abort") {
        const newThreadId_0 = uuidv4();
        log.info({
          errorCode: error.error.code,
          previousChatId: chatId,
          threadId: newThreadId_0,
          surface
        }, "copilot run failed");
        setThreadId(newThreadId_0);
        setChatId(null);
        clearPersistedUnlessProtected();
      }
      onError(error);
    };
    $[39] = chatId;
    $[40] = clearPersistedUnlessProtected;
    $[41] = onError;
    $[42] = surface;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const onErrorWithThreadReset = t12;
  let t13;
  if ($[44] !== clientId || $[45] !== urlChatId || $[46] !== userIdKey) {
    t13 = failedChatId => {
      if (!urlSeedUnconfirmedRef.current || failedChatId !== urlChatId) {
        return null;
      }
      const storedChatId_0 = readPersistedChatId(userIdKey, clientId);
      return storedChatId_0 === failedChatId ? null : storedChatId_0;
    };
    $[44] = clientId;
    $[45] = urlChatId;
    $[46] = userIdKey;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  const resolveFallbackChatId = t13;
  let t14;
  if ($[48] !== chatId || $[49] !== onChatHydrated || $[50] !== onChatIdChange || $[51] !== onErrorWithThreadReset || $[52] !== onLoadChat || $[53] !== onNewChat || $[54] !== onTitleGenerated || $[55] !== resolveFallbackChatId || $[56] !== threadId) {
    t14 = {
      threadId,
      chatId,
      onError: onErrorWithThreadReset,
      onChatIdChange,
      onNewChat,
      onLoadChat,
      onChatHydrated,
      onTitleGenerated,
      resolveFallbackChatId
    };
    $[48] = chatId;
    $[49] = onChatHydrated;
    $[50] = onChatIdChange;
    $[51] = onErrorWithThreadReset;
    $[52] = onLoadChat;
    $[53] = onNewChat;
    $[54] = onTitleGenerated;
    $[55] = resolveFallbackChatId;
    $[56] = threadId;
    $[57] = t14;
  } else {
    t14 = $[57];
  }
  return t14;
};